import React, { useRef } from 'react';
import { TextField, Box, Paper, Tooltip } from '@mui/material';
import MarkedText from './MarkedText';

// Two kinds of picker entries:
//  - `token`  : a single tag inserted at the caret; the button shows the tag
//               rendered by MarkedText (dice/results/force symbols).
//  - open/close: a formatting tag that wraps the current selection (or drops the
//               caret between the tags when nothing is selected). The button
//               shows a standard editor glyph.
const GROUPS = [
    {
        label: 'Dice',
        items: [
            { token: '[BO]', name: 'Boost' },
            { token: '[SE]', name: 'Setback' },
            { token: '[AB]', name: 'Ability' },
            { token: '[DI]', name: 'Difficulty' },
            { token: '[PR]', name: 'Proficiency' },
            { token: '[CH]', name: 'Challenge' },
            { token: '[FO]', name: 'Force' },
        ],
    },
    {
        label: 'Results',
        items: [
            { token: '[SU]', name: 'Success' },
            { token: '[FA]', name: 'Failure' },
            { token: '[AD]', name: 'Advantage' },
            { token: '[TH]', name: 'Threat' },
            { token: '[TR]', name: 'Triumph' },
            { token: '[DE]', name: 'Despair' },
        ],
    },
    {
        label: 'Force',
        items: [
            { token: '[LI]', name: 'Light Side' },
            { token: '[DA]', name: 'Dark Side' },
            { token: '[FP]', name: 'Force Point' },
            { token: '[CONFLICT]', name: 'Conflict' },
        ],
    },
    {
        label: 'Format',
        items: [
            { open: '[B]', close: '[b]', name: 'Bold', glyph: <b>B</b> },
            { open: '[I]', close: '[i]', name: 'Italic', glyph: <i>I</i> },
            { open: '[H3]', close: '[h3]', name: 'Heading', glyph: 'H3' },
            { open: '[H4]', close: '[h4]', name: 'Subheading', glyph: 'H4' },
            { open: '[P]', close: '', name: 'Paragraph break', glyph: '¶' },
            { open: '[BR]', close: '', name: 'Line break', glyph: '↵' },
        ],
    },
];

const SymbolField = ({ value, onChange, label, sx, ...props }) => {
    const inputRef = useRef(null);
    const text = value || '';

    // Insert `open`/`close` around the current selection, then restore focus and
    // place the caret sensibly. Single tags pass an empty `close`.
    const applyTag = (open, close = '') => {
        const el = inputRef.current;
        let start = text.length;
        let end = text.length;
        if (el && typeof el.selectionStart === 'number') {
            start = el.selectionStart;
            end = el.selectionEnd;
        }
        const selected = text.slice(start, end);
        const newVal = text.slice(0, start) + open + selected + close + text.slice(end);
        // Preserve the existing (event-based) onChange contract of MUI TextField.
        onChange({ target: { value: newVal } });
        requestAnimationFrame(() => {
            if (!el) return;
            el.focus();
            // Wrapping tag with no selection -> caret between the tags; otherwise
            // caret after everything that was inserted.
            const pos = (close && !selected)
                ? start + open.length
                : start + open.length + selected.length + close.length;
            el.setSelectionRange(pos, pos);
        });
    };

    const buttonSx = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '1.9em',
        cursor: 'pointer',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '4px',
        background: 'transparent',
        color: 'inherit',
        p: '2px 4px',
        fontSize: '1.1rem',
        lineHeight: 1,
        '&:hover': { borderColor: 'text.primary', bgcolor: 'action.hover' },
    };

    return (
        <Box sx={{ width: '100%', ...sx }}>
            <Paper
                variant="outlined"
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '4px',
                    p: '4px 6px',
                    mb: '4px',
                }}
            >
                {GROUPS.map((group, gi) => (
                    <React.Fragment key={group.label}>
                        {gi > 0 && (
                            <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: 'divider', mx: '2px' }} />
                        )}
                        {group.items.map((item) => {
                            const key = item.token || item.open;
                            const title = item.token
                                ? `${item.name}  ${item.token}`
                                : `${item.name}  ${item.open}${item.close ? '…' + item.close : ''}`;
                            const onClick = item.token
                                ? () => applyTag(item.token)
                                : () => applyTag(item.open, item.close);
                            return (
                                <Tooltip key={key} title={title} arrow>
                                    {item.token ? (
                                        <Box
                                            component="button"
                                            type="button"
                                            onClick={onClick}
                                            sx={buttonSx}
                                            dangerouslySetInnerHTML={{ __html: MarkedText.renderer(item.token) }}
                                        />
                                    ) : (
                                        <Box component="button" type="button" onClick={onClick} sx={buttonSx}>
                                            {item.glyph}
                                        </Box>
                                    )}
                                </Tooltip>
                            );
                        })}
                    </React.Fragment>
                ))}
            </Paper>

            <TextField
                fullWidth
                multiline
                inputRef={inputRef}
                label={label}
                value={text}
                onChange={onChange}
                {...props}
            />
        </Box>
    );
};

export default SymbolField;
